import { useEffect, useState, useCallback } from 'react';
import { useParams } from "react-router";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useOutletContext } from "react-router-dom";
import { Button, Paper, Typography, TextField, MenuItem, Select, FormControl, Checkbox, OutlinedInput, CircularProgress, InputLabel, Chip, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import LoadingButton from "@mui/lab/LoadingButton";
import DoneIcon from "@mui/icons-material/Done";
import SaveIcon from "@mui/icons-material/Save";
import useAPI from "hooks/useAPI.js";
import EnrollmentService from "services/EnrollmentService.js";
import PatientService from "services/PatientService.js";
import moment from 'moment';
import { useSlashCommands } from "hooks/useSlashCommands";
import SlashCommandDropdown from "components/inputs/SlashCommandDropdown";

const academicProgramOptions = [
    'IXL - Math',
    'IXL - Language Arts',
    'MaxScholar',
];

const gradeLevelOptions = [
    'Pre-Kindergarten', 'Kindergarten', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12',
];

const gradeLevelOptionsWithoutPreK = [
    'Kindergarten', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12',
];

const lessonCompletionOptions = [{ label: 'Yes (+)', value: 'Yes' }, { label: 'No (-)', value: 'No' }];
const equipmentOptions = [
    'N/A', 'Eyelights', 'Tens Unit', 'Laser', 'Transcranial', 'Galvanic Device', 'Bone Conducting Headphones',
];

const SessionAcademics = () => {
    let navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isCompleted = searchParams.get("isCompleted");
    const theme = useTheme();

    const [hospitalId] = useOutletContext();
    const { patientId } = useParams();
    const { enrollmentId } = useParams();
    const { sessionId } = useParams();


    const [academicPrograms, setAcademicPrograms] = useState([]);
    const [programDetails, setProgramDetails] = useState({});
    const [equipment, setEquipment] = useState([]);
    const [notes, setNotes] = useState('');
    const [goalLevels, setGoalLevels] = useState({});
    const location = useLocation();
    const sessionNumber = location.state?.sessionNumber;

    const [id, setId] = useState(0);

    const [sessionsAcademicsHistory, setSessionsAcademicsHistory] = useState(
        academicProgramOptions.reduce((acc, program) => {
            acc[program] = [];
            return acc;
        }, {})
    );


    const getSessionAcademicsAPI = useAPI(
        EnrollmentService.getSessionAcademics,
        getSessionAcademicsHandler,
    );

    const saveSessionAcademicAPI = useAPI(EnrollmentService.saveSessionAcademic, () => { });

    const submitSessionAPI = useAPI(
        EnrollmentService.submitEnrollmentSession,
        submitSessionHandler,
        true
    );

    function getSessionAcademics() {
        getSessionAcademicsAPI.execute(
            hospitalId,
            patientId,
            enrollmentId,
            sessionId
        );
    }

    const getPatientDetailsAPI = useAPI(
        PatientService.getPatient,
        getPatientDetailsHandler
    );

    function getPatientDetailsHandler(response) {
        const dob = response.data.dob;
        if (!dob) return;

        const age = moment().diff(moment(dob, "YYYY-MM-DD"), "years");

        const levels = {
            "IXL - Math": getIXLGoalLevel(age),
            "IXL - Language Arts": getIXLGoalLevel(age),
            "MaxScholar": getMaxScholarGoalLevel(age),
        };

        setGoalLevels(levels);
    }



    function getMaxScholarGoalLevel(age) {
        if (age <= 4) return "Kindergarten";
        if (age === 5) return "Grade 1";
        if (age === 6) return "Grade 2";
        if (age === 7) return "Grade 3";
        if (age === 8) return "Grade 4";
        if (age === 9) return "Grade 5";
        if (age === 10) return "Grade 6";
        if (age === 11) return "Grade 7";
        if (age === 12) return "Grade 8";
        if (age === 13) return "Grade 9";
        if (age === 14) return "Grade 10";
        if (age === 15) return "Grade 11";
        return "Grade 12";
    }

    function getIXLGoalLevel(age) {
        if (age <= 3) return "PK (A)";
        if (age === 4) return "K (B)";
        if (age === 5) return "1 (C)";
        if (age === 6) return "2 (D)";
        if (age === 7) return "3 (E)";
        if (age === 8) return "4 (F)";
        if (age === 9) return "5 (G)";
        if (age === 10) return "6 (H)";
        if (age === 11) return "7 (I)";
        if (age === 12) return "8 (J)";
        if (age === 13) return "9 (K)";
        if (age === 14) return "10 (L)";
        if (age === 15) return "11 (M)";
        return "12 (N)";
    }


    useEffect(() => {
        getPatientDetailsAPI.execute(hospitalId, patientId);
    }, []);

    async function getSessionsAcademicsHistory(program) {

        if (sessionsAcademicsHistory[program].length > 0) {
            return; // Already loaded, no need to fetch again
        }
        const response = await EnrollmentService.getSessionsAcademicsHistory([hospitalId,
            patientId,
            enrollmentId,
            sessionId,
            program]
        );

        let data = response.data;
        // Flatten the array if it's an array of arrays
        if (Array.isArray(data) && Array.isArray(data[0])) {
            data = data.flat();
        }

        setSessionsAcademicsHistory((prev) => {
            return {
                ...prev, [program]: data
            }
        });

        return data;
    }


    function saveSession() {

        // Call saveSession first with the updated academicPrograms
        const payload = {
            equipment: equipment.join(", ") || null,
            id: id,

            data: academicPrograms.map((program) => ({
                program,
                gradeLevel: programDetails[program]?.gradeLevel || '',
                lessonWorkedOn: programDetails[program]?.lessonWorkedOn || '',
                lessonCompletion: programDetails[program]?.lessonCompletion || '',
                id: programDetails[program]?.id || 0,
                session_academic_id: programDetails[program]?.sessionAcademicId || 0,
            })),
            notes: notes || '',
        };

        saveSessionAcademicAPI.execute(hospitalId, patientId, enrollmentId, sessionId, payload);
    }

    const handleSubmit = () => {
        saveSession();
        submitSessionAPI.execute(hospitalId, patientId, enrollmentId, sessionId);
    };

    // Handler for academic program checkbox change
    async function handleAcademicProgramChange(optionValue, checked) {
        let newValue;
        if (checked) {
            newValue = [...academicPrograms, optionValue];
        } else {
            newValue = academicPrograms.filter((v) => v !== optionValue);
        }

        setAcademicPrograms(newValue);

        let history = await getSessionsAcademicsHistory(optionValue)

        // Compute the grade level for the newly added program (based on past sessions)
        handleProgramDetailChange(optionValue, 'gradeLevel', computeGradeLevel(optionValue, history));

    }

    // Handler for equipment checkbox change
    function handleEquipmentChange(optionValue, checked) {
        let newValue;
        if (checked) {
            newValue = [...equipment, optionValue];
        } else {
            newValue = equipment.filter((v) => v !== optionValue);
        }
        // Then update the state
        setEquipment(newValue);
    }

    function handleProgramDetailChange(program, field, value) {
        const updatedProgramDetails = {
            ...programDetails,
            [program]: {
                ...programDetails[program],
                [field]: value,
            },
        };

        setProgramDetails(updatedProgramDetails);
    }

    useEffect(() => {
        getSessionAcademics();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /* API Success Handlers */

    function getSessionAcademicsHandler(response) {
        // Add null check for response.data
        const data = response.data || {};
        // If sessionAcademicDto is missing or not an array, use empty array
        const academicDetails = Array.isArray(data.sessionAcademicDto) ? data.sessionAcademicDto : [];

        // Set academicPrograms as the list of program values (exclude retired options)
        const loadedPrograms = academicDetails
            .map(item => item.program)
            .filter((program) => academicProgramOptions.includes(program));

        setAcademicPrograms(loadedPrograms);

        // Set programDetails as an object keyed by program
        const details = {};
        academicDetails.forEach(item => {
            if (!academicProgramOptions.includes(item.program)) return;
            details[item.program] = {
                gradeLevel: item.gradeLevel || '',
                lessonWorkedOn: item.lessonWorkedOn || '',
                lessonCompletion: item.lessonCompletion || '',
                id: item.id,
                sessionAcademicId: item.sessionAcademicId
            };
        });

        setProgramDetails(details);

        // Set notes and equipment safely
        setNotes(data.notes || '');
        setId(data.id || 0);

        if (data.equipment) {
            setEquipment(data.equipment.split(',').map(e => e.trim()));
        } else {
            setEquipment([]);
        }

        // Fetch history for each program
        for (const program of loadedPrograms) {
            getSessionsAcademicsHistory(program);
        }
    }

    function submitSessionHandler(response) {
        navigate(-1);
    }

    function computeGradeLevel(program, history) {
        if (programDetails[program]?.gradeLevel) {
            return programDetails[program].gradeLevel;
        }

        // Determine which grade level options to use based on the program
        let availableGradeLevels;
        if (program === 'IXL - Math' || program === 'IXL - Language Arts') {
            availableGradeLevels = gradeLevelOptions;
        } else {
            availableGradeLevels = gradeLevelOptionsWithoutPreK;
        }
        // Check if the last 10 consecutive sessions have 'Yes' lessonCompletions

        if (history.length >= 10) {
            // Get the last 10 sessions
            const last10 = history.slice(-10);
            const allYes = last10.every(s => s.lessonCompletion === 'Yes');
            const allNo = last10.every(s => s.lessonCompletion === 'No');
            const allSameLevel = last10.every(s => s.gradeLevel === last10[0].gradeLevel);

            // Find the last session with a gradeLevel
            const lastSession = [...history].reverse().find(s => s.gradeLevel);
            let currentLevel = lastSession ? lastSession.gradeLevel : '';

            const idx = availableGradeLevels.indexOf(currentLevel);

            if (!allSameLevel)
                return currentLevel;

            if (allYes) {
                if (idx !== -1 && idx < availableGradeLevels.length - 1) {
                    return availableGradeLevels[idx + 1];
                }
                // If already at max, return current
                return currentLevel;
            }

            if (allNo) {
                if (idx > 0) {
                    return availableGradeLevels[idx - 1];
                }
                // If already at min, return current
                return currentLevel;
            }
        }

        // If no level is set, return an empty string
        if (!programDetails[program]) {
            return '';
        }
    }

    // State to control "Saved" message visibility
    const [showSaved, setShowSaved] = useState(false);

    const setFieldValue = useCallback((name, value) => {
        if (name === 'notes') {
            setNotes(value);
        } else if (name.startsWith('lessonWorkedOn_')) {
            const program = name.substring('lessonWorkedOn_'.length);
            setProgramDetails(prev => ({
                ...prev,
                [program]: { ...prev[program], lessonWorkedOn: value },
            }));
        }
    }, []);

    const { getSlashProps, dropdownState, applyShortcut } = useSlashCommands(setFieldValue);

    // Effect to auto-hide the "Saved" message after 2 seconds
    useEffect(() => {
        if (saveSessionAcademicAPI.status === "success") {
            setShowSaved(true);
            getSessionAcademics();

            const timer = setTimeout(() => setShowSaved(false), 2000);
            return () => clearTimeout(timer);
        }
    }, [saveSessionAcademicAPI.status]);

    return (
        <Paper elevation={0} className="p-4">
            <div className="d-flex justify-content-between">
                <Typography variant="h2" gutterBottom>
                    Session Academics {sessionNumber && `- Session #${sessionNumber}`}
                </Typography>
                <Button onClick={() => navigate(-1)} variant="outlined">
                    Back
                </Button>
            </div>
            {getSessionAcademicsAPI.status === "pending" ? (
                <div className="text-center">
                    <CircularProgress
                        className="text-center"
                        sx={{
                            my: 20,
                        }}
                    />
                </div>
            ) :
                (<form className="mt-2">
                    <div className="row mb-4">
                        <div className="col-12 col-md-5">
                            <Typography color="text.secondary" variant="subtitle1" component="span" className="me-4">
                                Academic Programs
                            </Typography>
                            <FormControl fullWidth>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    {academicProgramOptions.map((option) => (
                                        <div key={option}>
                                            <Checkbox
                                                checked={academicPrograms.includes(option)}
                                                onChange={(e) => handleAcademicProgramChange(option, e.target.checked)}
                                            />
                                            <span>{option}</span>
                                        </div>
                                    ))}
                                </div>
                            </FormControl>
                        </div>
                        <div className="col-12 col-md-7">
                            <Typography color="text.secondary" variant="subtitle1">
                                Equipment
                            </Typography>
                            <FormControl fullWidth>
                                <div className="container-fluid">
                                    <div className="row">
                                        {equipmentOptions.map((option, idx) => (
                                            <div className="col-6" key={option} style={{ marginBottom: 4 }}>
                                                <Checkbox
                                                    checked={equipment.includes(option)}
                                                    onChange={(e) => handleEquipmentChange(option, e.target.checked)}
                                                />
                                                <span>{option}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </FormControl>
                        </div>
                    </div>
                    {academicPrograms.map((program) => (
                        <div key={program} className="mb-4">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <Typography variant="subtitle1" className="mb-2">
                                    {program}
                                </Typography>
                                {goalLevels[program] && (
                                    <Paper elevation={1} sx={{ p: 2, mb: 2, backgroundColor: theme.palette.background.paper }}>
                                        <Stack direction="column" spacing={3} alignItems="center">
                                            <Stack direction="row" spacing={3} alignItems="center">
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    Goal Level:
                                                </Typography>
                                                <Chip
                                                    color="success"
                                                    label={goalLevels[program]}
                                                    size="medium"
                                                    sx={{ fontWeight: 'bold' }}
                                                />
                                            </Stack>
                                        </Stack>
                                    </Paper>
                                )}
                            </div>
                            <div />

                            {/* History for this program - styled UI */}
                            {sessionsAcademicsHistory[program].length > 0 && (() => {

                                const history = sessionsAcademicsHistory[program];

                                if (!history.length) return null;
                                return (
                                    <div style={{ marginBottom: 16 }}>
                                        <Typography color="text.secondary" variant="h6" sx={{ mb: 1 }}>
                                            History
                                        </Typography>
                                        <Paper elevation={1} sx={{ px: 2, py: 2, background: '#f7fafd', borderRadius: 2, display: 'inline-block', minWidth: 0 }}>
                                            <div style={{ display: 'grid', gridAutoFlow: 'column', gridTemplateRows: `repeat(3, auto)`, gridTemplateColumns: `max-content`, gap: 8, alignItems: 'start' }}>
                                                {/* Row 1: Headers */}
                                                <Typography variant="subtitle2" sx={{ color: '#1976d2', fontWeight: 600 }}>Session</Typography>
                                                <Typography variant="subtitle2" sx={{ color: '#1976d2', fontWeight: 600 }}>
                                                    Grade Level
                                                </Typography>
                                                <Typography variant="subtitle2" sx={{ color: '#1976d2', fontWeight: 600 }}>Lesson Completion</Typography>
                                                {/* Columns: Each session's values vertically */}
                                                {history.map((session, idx) => (
                                                    <div key={`session-${idx}`} style={{ display: 'contents' }}>
                                                        <Typography
                                                            key={"session-history-" + idx}
                                                            variant="body1"
                                                            sx={{ color: '#555', fontWeight: 500, px: 2, py: 0.5, borderRadius: 1, background: '#e3f2fd', textAlign: 'center', minWidth: 40 }}
                                                        >
                                                            {idx + 1}
                                                        </Typography>
                                                        <Typography
                                                            key={"grade-history-" + idx}
                                                            variant="body1"
                                                            sx={{ color: '#333', fontWeight: 500, px: 2, py: 0.5, borderRadius: 1, background: '#fffde7', textAlign: 'center', minWidth: 40 }}
                                                        >
                                                            {session.gradeLevel}
                                                        </Typography>
                                                        <Typography
                                                            key={"lesson-completion-history-" + idx}
                                                            variant="body1"
                                                            sx={{ color: session.lessonCompletion === 'Yes' ? '#388e3c' : '#d32f2f', fontWeight: 500, px: 2, py: 0.5, borderRadius: 1, background: session.lessonCompletion === 'Yes' ? '#e8f5e9' : '#ffebee', textAlign: 'center', minWidth: 40 }}
                                                        >
                                                            {session.lessonCompletion}
                                                        </Typography>
                                                    </div>
                                                ))}
                                            </div>
                                        </Paper>
                                    </div>
                                );
                            })()}
                            <div className="row">
                                <div className="col-md-3">
                                    <FormControl fullWidth>
                                        <InputLabel>
                                            Grade Level
                                        </InputLabel>
                                        <Select
                                            value={programDetails[program]?.gradeLevel || ''}
                                            onChange={(e) => handleProgramDetailChange(program, 'gradeLevel', e.target.value)}
                                            input={<OutlinedInput label="Grade Level" />}
                                        >
                                            {(program === 'IXL - Math' || program === 'IXL - Language Arts' ? gradeLevelOptions : gradeLevelOptionsWithoutPreK
                                                ).map((level) => (
                                                    <MenuItem key={level} value={level}>
                                                        {level}
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                    </FormControl>
                                </div>
                                <div className="col-md-6">
                                    <TextField
                                        fullWidth
                                        label="Lesson/Skills Worked on"
                                        variant="outlined"
                                        value={programDetails[program]?.lessonWorkedOn || ''}
                                        {...getSlashProps(`lessonWorkedOn_${program}`, (e) => handleProgramDetailChange(program, 'lessonWorkedOn', e.target.value))}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <Typography color="text.secondary" variant="subtitle2" sx={{ mt: '-15px', mb: 1.5 }}>
                                        Lesson/s Completion
                                    </Typography>
                                    {lessonCompletionOptions.map((option) => (
                                        <label
                                            key={option.value}
                                            htmlFor={`lessonCompletion-${program}-${option.value}`}
                                            style={{
                                                display: 'inline-block',
                                                padding: '6px 18px',
                                                borderRadius: '5px',
                                                border: (programDetails[program]?.lessonCompletion === option.value) ? '2px solid #3482d1' : '1px solid #ccc',
                                                background: (programDetails[program]?.lessonCompletion === option.value) ? '#3482d1' : '#fff',
                                                color: (programDetails[program]?.lessonCompletion === option.value) ? '#fff' : '#333',
                                                fontWeight: 500,
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                marginRight: 8,
                                            }}
                                        >
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name={`lessonCompletion-${program}`}
                                                id={`lessonCompletion-${program}-${option.value}`}
                                                value={option.value}
                                                onChange={() => handleProgramDetailChange(program, 'lessonCompletion', option.value)}
                                                checked={programDetails[program]?.lessonCompletion === option.value}
                                                style={{ display: 'none' }}
                                            />
                                            {option.label}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}

                    {/* Notes Text Box */}
                    <div className="row mb-4">
                        <div className="col-12">
                            <TextField
                                label="Notes"
                                variant="outlined"
                                fullWidth
                                multiline
                                minRows={3}
                                value={notes}
                                {...getSlashProps('notes', (e) => setNotes(e.target.value))}
                            />
                        </div>
                    </div>
                    {
                        isCompleted === "false" && (
                            <div className="d-flex align-items-center mb-3">
                                <LoadingButton
                                    loading={saveSessionAcademicAPI.status === "pending"}
                                    loadingPosition="start"
                                    startIcon={<SaveIcon />}
                                    variant="outlined"
                                    className="me-3"
                                    onClick={saveSession}
                                    type="button"
                                >
                                    Save
                                </LoadingButton>

                                {submitSessionAPI.status === "pending" ? (
                                    <LoadingButton
                                        loading
                                        loadingPosition="start"
                                        startIcon={<SaveIcon />}
                                        variant="contained"
                                        className="me-3"
                                        style={{ marginLeft: 8 }}
                                    >
                                        Submit
                                    </LoadingButton>
                                ) : (
                                    <Button
                                        type="button"
                                        className="me-3"
                                        startIcon={<DoneIcon />}
                                        variant="contained"
                                        onClick={handleSubmit}
                                        style={{ marginLeft: 4 }}
                                    >
                                        Submit
                                    </Button>
                                )}
                                {showSaved && (
                                    <Typography color="success.main">Saved</Typography>
                                )}
                            </div>
                        )}

                </form>)}
            <SlashCommandDropdown state={dropdownState} onSelect={applyShortcut} />
        </Paper>
    );
};

export default SessionAcademics;